import { createReducer, on } from '@ngrx/store';
import { Field, FormBuilderConfig } from '../models/form-builder.models';
import { UploadedFile } from '../../shared/lib/form/ni-file-upload/ni-file-upload.component';
import * as FormBuilderActions from './form-builder.actions';

export interface FormBuilderState {
  config: FormBuilderConfig | null;
  formValues: Record<string, any>;
  currentSectionId: string;
  isLoading: boolean;
  isSubmitting: boolean;
  error: any | null;
  submitSuccess: boolean;
  submitResponse: any | null;
  uploadedFilesMap: Record<string, UploadedFile[]>;
  isStatic?: boolean;
  staticConfig?: any;
}

export const initialState: FormBuilderState = {
  config: null,
  formValues: {},
  currentSectionId: '',
  isLoading: false,
  isSubmitting: false,
  error: null,
  submitSuccess: false,
  submitResponse: null,
  uploadedFilesMap: {},
  isStatic: false,
  staticConfig: null,
};

export const formBuilderReducer = createReducer(
  initialState,

  // Load Form Config
  on(FormBuilderActions.loadFormConfig, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),

  on(FormBuilderActions.loadFormConfigSuccess, (state, { config }) => {
    // Initialize form values from config
    const formValues: Record<string, any> = {};
    
    if (config.sections) {
      config.sections.forEach(section => {
        section.groups?.forEach(group => {
          // Regular fields
          group.fields?.forEach(field => {
            if (field.value !== null && field.value !== undefined) {
              // For checkbox fields, extract 'value' key from objects
              if (field.type === 'checkbox') {
                if (Array.isArray(field.value)) {
                  // Extract 'value' key from each item if it's an object
                  formValues[field.field_id] = field.value.map((item: any) => {
                    if (typeof item === 'object' && item !== null && item.value !== undefined) {
                      return item.value;
                    }
                    return item;
                  });
                } else if (typeof field.value === 'object' && field.value.value !== undefined) {
                  // Single object with 'value' key
                  formValues[field.field_id] = [field.value.value];
                } else {
                  // Already a value or array of values
                  formValues[field.field_id] = Array.isArray(field.value) ? field.value : [field.value];
                }
              } else {
                // Convert date strings to Date objects for datepicker fields
                if (field.type === 'datepicker' && field.value) {
                  const dateValue = field.value;
                  if (typeof dateValue === 'string' || typeof dateValue === 'number') {
                    const date = new Date(dateValue);
                    formValues[field.field_id] = !isNaN(date.getTime()) ? date : null;
                  } else if (dateValue instanceof Date) {
                    formValues[field.field_id] = dateValue;
                  } else {
                    formValues[field.field_id] = null;
                  }
                } else {
                formValues[field.field_id] = field.value;
                }
              }
            }
          });
          
          // Table data
          if (group.is_table && group.table_data) {
            formValues[`table_${group.group_id}`] = group.table_data;
          }
        });
      });
    }

    const firstSectionId = config.sections && config.sections.length > 0 
      ? config.sections[0].section_id 
      : '';

    // Check if this is a static program
    const isStatic = (config as any).is_static === true;
    const staticConfig = isStatic ? (config as any).static_config : null;


    // Check if this is a static program
    // const isStatic = 'is_static' in config && config.is_static === true;
    // const staticConfig = isStatic && 'static_config' in config ? config.static_config : null;
    

    return {
      ...state,
      config,
      formValues,
      currentSectionId: firstSectionId,
      isLoading: false,
      error: null,
      isStatic,
      staticConfig,
    };
  }),


  on(FormBuilderActions.loadFormConfigFailure, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Update Form Values
  on(FormBuilderActions.updateFormValue, (state, { fieldId, value }) => ({
    ...state,
    formValues: {
      ...state.formValues,
      [fieldId]: value,
    },
  })),

  on(FormBuilderActions.updateFormValues, (state, { values }) => ({
    ...state,
    formValues: {
      ...state.formValues,
      ...values,
    },
  })),

  on(FormBuilderActions.updateTableData, (state, { groupId, tableData }) => {
    // Update formValues
    const updatedFormValues = {
      ...state.formValues,
      [`table_${groupId}`]: tableData,
    };

    // Also update the config's group.table_data so the table can display it
    if (state.config && state.config.sections) {
      const updatedConfig = {
        ...state.config,
        sections: state.config.sections.map(section => ({
          ...section,
          groups: section.groups?.map(group => {
            if (group.is_table && group.group_id === groupId) {
              return { ...group, table_data: tableData };
            }
            return group;
          })
        }))
      };

      return {
        ...state,
        config: updatedConfig,
        formValues: updatedFormValues,
      };
    }

    return {
      ...state,
      formValues: updatedFormValues,
    };
  }),

  on(FormBuilderActions.updateGroupVisibility, (state, { groupId, isVisible, sectionId }) => {
    // Update the config's group.is_visible so the group visibility is reflected in the UI
    // If sectionId is provided, only update groups in that section (prevents affecting duplicate group IDs in other sections)
    
    
    if (state.config && state.config.sections) {
      const updatedConfig = {
        ...state.config,
        sections: state.config.sections.map(section => {
          // If sectionId is provided, only update groups in that section
          if (sectionId && section.section_id !== sectionId) {
            return section;
          }
          
          return {
            ...section,
            groups: section.groups?.map(group => {
              if (group.group_id === groupId) {
                // If sectionId is provided, only update if we're in the right section
                // If sectionId is not provided (backward compatibility), update all groups with this ID
                if (!sectionId || section.section_id === sectionId) {
              
                  return { ...group, is_visible: isVisible };
                }
              }
              return group;
            })
          };
        })
      };

      
      return {
        ...state,
        config: updatedConfig,
      };
    }

    
    return state;
  }),

  on(FormBuilderActions.updateCurrentSection, (state, { sectionId }) => ({
    ...state,
    currentSectionId: sectionId,
  })),

  // Submit Form
  on(FormBuilderActions.submitForm, (state) => ({
    ...state,
    isSubmitting: true,
    error: null,
    submitSuccess: false,
  })),

  on(FormBuilderActions.submitFormSuccess, (state, { response }) => ({
    ...state,
    isSubmitting: false,
    submitSuccess: true,
    error: null,
    submitResponse: response,
  })),

  on(FormBuilderActions.submitFormFailure, (state, { error }) => ({
    ...state,
    isSubmitting: false,
    error,
    submitSuccess: false,
  })),

  // Add Group to Section
  on(FormBuilderActions.addGroupToSection, (state, { sectionId, group }) => {
    if (state.config && state.config.sections) {
      const updatedConfig = {
        ...state.config,
        sections: state.config.sections.map(section => {
          if (section.section_id === sectionId) {
            return {
              ...section,
              groups: [...(section.groups || []), group]
            };
          }
          return section;
        })
      };

      return {
        ...state,
        config: updatedConfig,
      };
    }

    return state;
  }),

  // Update Group Data
  on(FormBuilderActions.updateGroupData, (state, { sectionId, groupId, data }) => {
    if (state.config && state.config.sections) {
      const updatedConfig = {
        ...state.config,
        sections: state.config.sections.map(section => {
          if (section.section_id === sectionId) {
            return {
              ...section,
              groups: section.groups?.map(group => {
                if (group.group_id === groupId) {
                  const existingData = (group as any).data || {};
                  return {
                    ...group,
                    data: { ...existingData, ...data }
                  };
                }
                return group;
              })
            };
          }
          return section;
        })
      };

      return {
        ...state,
        config: updatedConfig,
      };
    }

    return state;
  }),

  // Update Uploaded Files Map
  on(FormBuilderActions.updateUploadedFilesMap, (state, { uploadedFilesMap }) => ({
    ...state,
    uploadedFilesMap,
  })),

  // Reset Form
  on(FormBuilderActions.resetForm, () => initialState)
);

// Helper functions to reduce cognitive complexity
function initializeFormValuesFromConfig(config: FormBuilderConfig): Record<string, any> {
  const formValues: Record<string, any> = {};
  
  if (!config.sections) {
    return formValues;
  }

  config.sections.forEach(section => {
    section.groups?.forEach(group => {
      processGroupFields(group, formValues);
      processGroupTableData(group, formValues);
    });
  });

  return formValues;
}

function processGroupFields(group: any, formValues: Record<string, any>): void {
  group.fields?.forEach((field: Field) => {
    if (field.value === null || field.value === undefined) {
      return;
    }

    const processedValue = processFieldValue(field);
    if (processedValue !== null && processedValue !== undefined) {
      formValues[field.field_id] = processedValue;
    }
  });
}

function processFieldValue(field: Field): any {
  if (field.type === 'checkbox') {
    return processCheckboxValue(field.value);
  }

  if (field.type === 'datepicker' && field.value) {
    return processDatepickerValue(field.value);
  }

  return field.value;
}

function processCheckboxValue(value: any): any {
  if (Array.isArray(value)) {
    return value.map((item: any) => {
      if (typeof item === 'object' && item !== null && item.value !== undefined) {
        return item.value;
      }
      return item;
    });
  }

  if (typeof value === 'object' && value !== null && value.value !== undefined) {
    return [value.value];
  }

  return Array.isArray(value) ? value : [value];
}

function processDatepickerValue(value: any): Date | null {
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return !isNaN(date.getTime()) ? date : null;
  }

  if (value instanceof Date) {
    return value;
  }

  return null;
}

function processGroupTableData(group: any, formValues: Record<string, any>): void {
  if (group.is_table && group.table_data) {
    formValues[`table_${group.group_id}`] = group.table_data;
  }
}

function getFirstSectionId(config: FormBuilderConfig): string {
  if (config.sections && config.sections.length > 0) {
    return config.sections[0].section_id;
  }
  return '';
}

function extractStaticConfig(config: FormBuilderConfig): { isStatic: boolean; staticConfig: any } {
  const isStatic = 'is_static' in config && config['is_static' as keyof typeof config] === true;
  const staticConfig = isStatic && 'static_config' in config 
    ? config['static_config' as keyof typeof config] 
    : null;
  
  return { isStatic, staticConfig };
}

